// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { TFunction } from 'i18next';
import React, { ReactElement } from 'react';
import {
  trLightOrange,
  trLightPink,
  trLightPurple,
  trLightViolet,
  trLime,
} from 'components/treactui-template/atoms/AdditionalColors';
import { Color } from '@tls/sw91-communication/types/com.base';
import { Colors } from '@tls/treact-ui';
import { IconStop } from '@tls/treact-ui/icons';

export type ColorOptionType = {
  id: string;
  label: string;
  icon: ReactElement;
};

function colorToId(color?: Color) {
  if (!color) return Colors.trblue2.rgb;
  return `${color.red},${color.green},${color.blue}`;
}

export function IdToColor(id: string): Color {
  const splitId = id.split(',');
  if (splitId.length !== 3) return IdToColor(Colors.trblue2.rgb);

  return {
    red: Number.parseInt(splitId[0]),
    green: Number.parseInt(splitId[1]),
    blue: Number.parseInt(splitId[2]),
  };
}

export function getColorOption(color?: Color) {
  const id = colorToId(color);
  return colorOptions.find(c => c.id === id) ?? colorOptions[0];
}
export const getBackgroundColorOption = (): ColorOptionType => colorOptions[6]; // Background is always black

export const colorOptions: ColorOptionType[] = [
  {
    id: Colors.trblue2.rgb,
    label: 'color.blue',
    icon: <IconStop key='blue' color={Colors.trblue2.hex} height='1.5rem' />,
  },
  {
    id: trLightOrange.rgb,
    label: 'color.orange',
    icon: <IconStop key='orange' color={trLightOrange.hex} height='1.5rem' />,
  },
  { id: trLime.rgb, label: 'color.lime', icon: <IconStop key='lime' color={trLime.hex} height='1.5rem' /> },
  {
    id: trLightPurple.rgb,
    label: 'color.purple',
    icon: <IconStop key='purple' color={trLightPurple.hex} height='1.5rem' />,
  },
  {
    id: trLightViolet.rgb,
    label: 'color.violet',
    icon: <IconStop key='violet' color={trLightViolet.hex} height='1.5rem' />,
  },
  {
    id: trLightPink.rgb,
    label: 'color.pink',
    icon: <IconStop key='pink' color={trLightPink.hex} height='1.5rem' />,
  },
  {
    id: '0,0,0',
    label: 'color.background',
    icon: <IconStop key='background' color={'#000000'} height='1.5rem' />,
  },
];

export const defaultColorId: Color = {
  red: Number.parseInt(colorOptions[0].id.split(',')[0]),
  green: Number.parseInt(colorOptions[0].id.split(',')[1]),
  blue: Number.parseInt(colorOptions[0].id.split(',')[2]),
};

export const colorKeySelector = (o: ColorOptionType): React.Key => o.id;
export const colorLabelSelector = (t: TFunction) => (o: ColorOptionType) => t(o.label);
export const colorIconSelector = (o: ColorOptionType) => o.icon;
